<template>
  <WdsStack v-if="showForm" direction-column gap-md>
    <WdsAlert v-if="isUmbrellaAccount">
      <div class="tw-mb-2">
        {{ $t('You are working under an umbrella company.') }}
      </div>
      <div>
        {{
          $t(
            'Please enter the bank account details of your Umbrella Company only. Other types of bank accounts will not be accepted for payment.',
          )
        }}
      </div>
    </WdsAlert>

    <WdsStack direction-column gap-sm>
      <WdsSelect
        :start-selected="form.bank_country"
        :list="filteredCountries"
        :placeholder="$t('Select the country')"
        :label="$t('Country of bank')"
        searchable
        @select-option="handleCountrySelected"
      />

      <div>
        <WdsButtonGhost v-if="!showAllCountries" secondary @click="showInternationalOptions">
          {{ $t('Show international options') }}
        </WdsButtonGhost>

        <WdsButtonGhost v-if="showAllCountries" secondary @click="hideInternationalOptions">
          {{ $t('Hide international options') }}
        </WdsButtonGhost>
      </div>
    </WdsStack>

    <WdsAlert v-if="!countryIsSupported">
      {{
        $t(
          'Unfortunately we do not support automatic transfers to :country. Please contact our support, and we will find a solution.',
          { country: countryName },
        )
      }}
    </WdsAlert>

    <template v-if="countryIsSupported">
      <WdsAlert v-if="countryIsHidden">
        {{
          $t(
            'We will review your bank account data and we may require you to setup a local bank account prior to completing any payments.',
          )
        }}
      </WdsAlert>

      <WdsStack direction-column gap-sm>
        <WdsStack direction-row class="tw-justify-between">
          <WdsText>
            {{ $t(`Account holder's name`) }}
          </WdsText>
          <WdsTooltip class="tw-justify-end">
            <template #tooltip-content>
              {{
                $t(
                  "Please enter the name exactly as it is registered with your bank. This will either be your own name or, if it's a business account, the name of your company.",
                )
              }}
            </template>
            <WdsIcon name="help" size="16" />
          </WdsTooltip>
        </WdsStack>

        <WdsInput
          v-model="form.beneficiary_name"
          name="beneficiary_name"
          :error="errors['beneficiaryName']"
          lg
          :required="hasDirtyFields"
        />
      </WdsStack>

      <WdsInput
        v-model="form.bank_name"
        name="bank_name"
        :label="$t('Name of bank')"
        :error="errors['bankName']"
        lg
        required
      />

      <template v-if="bban">
        <WdsInput
          v-for="(field, key) in bban"
          :key="camelCase(key)"
          v-model="form.bban[key]"
          :name="`bban_${form.bank_country}.${camelCase(key)}`"
          :label="$t(field.name)"
          :error="errors[`bban.${camelCase(key)}`]"
          lg
          :required="hasDirtyFields"
          :readonly="field.readonly"
        />

        <WdsInput
          v-if="selectedCountry.bban_swift"
          v-model="form.swift"
          name="swift"
          :label="$t('SWIFT')"
          :error="errors['swift']"
          lg
        />
      </template>

      <WdsStack v-if="showIban && bban" direction-column gap-xs>
        <WdsHeading type="subtitle">{{ $t('International bank details') }}</WdsHeading>

        <WdsText
          :text="$t('We recommend that you use a local bank account instead of IBAN.')"
          secondary
        />
      </WdsStack>

      <WdsInput
        v-if="showIban && selectedCountry.bban_swift"
        v-model="form.swift"
        name="swift"
        :label="$t('SWIFT')"
        :error="errors['swift']"
        lg
      />

      <WdsInput
        v-if="showIban"
        v-model="form.iban"
        name="iban"
        :label="$t('IBAN')"
        :error="errors['iban']"
        lg
        :required="hasDirtyFields"
      />

      <WdsInput
        v-if="showIban"
        v-model="form.swift"
        name="swift"
        :label="$t('SWIFT/BIC')"
        :error="errors['swift']"
        lg
        :required="hasDirtyFields"
      />

      <WdsStack v-if="countryIsHidden" direction-column gap-sm>
        <WdsInputArea
          v-model="form.bank_address"
          name="bank_address"
          :label="$t('Bank address')"
          :error="errors['bankAddress']"
          height="80px"
          :required="hasDirtyFields"
        />

        <WdsText
          :text="
            $t(
              'Enter the full address incl. postcode of the bank, where the bank account is registered.',
            )
          "
          tertiary
        />
      </WdsStack>
    </template>
  </WdsStack>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue'
import { camelCase } from 'lodash'
import {
  WdsAlert,
  WdsStack,
  WdsHeading,
  WdsText,
  WdsInput,
  WdsInputArea,
  WdsSelect,
  WdsButtonGhost,
  WdsIcon,
  WdsTooltip,
} from '@worksome/wds-components'
import { $t } from '@/composables/translation'

const props = defineProps({
  errors: {
    type: Object,
    required: true,
  },

  bankAccount: {
    type: Object,
    required: true,
  },

  countries: {
    type: Object,
    required: true,
  },

  isUmbrellaAccount: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['update:form', 'update:valid-country'])

const showForm = ref(false)
const showAllCountries = ref(false)

const form = ref({
  bank_country: props.bankAccount.bank_country,
  bank_name: '',
  bank_address: '',
  bban: {},
  beneficiary_name: '',
  swift: '',
  iban: '',
})

const filteredCountries = computed(() => {
  const countries = []

  for (const [key, value] of Object.entries(props.countries)) {
    if (!value.hidden || showAllCountries.value) {
      countries.push({
        label: value.name,
        value: key,
      })
    }
  }

  return countries
})

const selectedCountry = computed(() => props.countries[form.value.bank_country])

const bban = computed(() => {
  if (props.countries[form.value.bank_country] === undefined) {
    return null
  }

  return props.countries[form.value.bank_country].bban_fields
})

const supportsIban = computed(() => {
  if (props.countries[form.value.bank_country] === undefined) {
    return false
  }

  return props.countries[form.value.bank_country].supports_iban
})

const showIban = computed(() => {
  if (!supportsIban.value) {
    return false
  }

  // If BBAN is not supported, show IBAN.
  if (!bban.value) {
    return true
  }

  // If IBAN is defined, show IBAN
  if (
    props.bankAccount.iban ||
    (props.bankAccount.swift && props.countries[form.value.bank_country].bban_swift !== true)
  ) {
    return true
  }

  return false
})

// We presume that if a country where to for some reason be a wrong type,
// we would treat that case as if the country is hidden
const countryIsHidden = computed(() => props.countries[form.value.bank_country]?.hidden ?? true)

const countryIsSupported = computed(() => supportsIban.value || bban.value !== null)

const countryName = computed(() => props.countries[form.value.bank_country]?.name ?? '')

const getBbanAttribute = (name) => {
  if (
    props.bankAccount.bank_country !== form.value.bank_country ||
    props.bankAccount.bban === undefined ||
    props.bankAccount.bban === null
  ) {
    return ''
  }

  return props.bankAccount.bban[name]
}

/**
 * Bban fields on bank account require their own individual
 * getters which may be null so we assess them here
 */
const bankAccountSaturated = computed(() => {
  const bankAccount = { ...props.bankAccount, bban: {} }

  if (bban.value) {
    Object.keys(bban.value).forEach((key) => {
      const bbanFieldValue = getBbanAttribute(key)

      if (bbanFieldValue) {
        bankAccount.bban[key] = bbanFieldValue
      }

      if (bankAccount.bban[key] === undefined && bban.value[key].default !== undefined) {
        bankAccount.bban[key] = bban.value[key].default

        form.value.bban[key] = bban.value[key].default
      }
    })
  }

  return bankAccount
})

const hasDirtyBbanFields = computed(
  () =>
    bban.value &&
    Object.keys(form.value.bban).some(
      (key) =>
        form.value.bban?.[key] && form.value.bban?.[key] !== bankAccountSaturated.value.bban?.[key],
    ),
)

const hasDirtyFields = computed(() => {
  if (form.value.bank_country !== props.bankAccount.bank_country) {
    return true
  }

  if (hasDirtyBbanFields.value) {
    return true
  }

  return Object.keys(form.value).some(
    (key) =>
      typeof form.value[key] !== 'object' &&
      form.value[key] &&
      form.value[key] !== bankAccountSaturated.value[key],
  )
})

const showInternationalOptions = () => {
  showAllCountries.value = true
}

const hideInternationalOptions = () => {
  showAllCountries.value = false

  // If the previously select country is no longer available,
  // change it to the first available one.
  if (countryIsHidden.value) {
    form.value.bank_country = filteredCountries.value[0].value
  }
}

const handleCountrySelected = (val) => {
  form.value.bank_country = val.value
}

const handleFormUpdate = () => emit('update:form', form.value)
const handleCountryUpdated = () => emit('update:valid-country', countryIsSupported.value)

watch(form.value, () => handleFormUpdate())
watch(countryIsSupported, () => handleCountryUpdated())

onMounted(() => {
  if (countryIsHidden.value) {
    showAllCountries.value = true
  }

  form.value = {
    bank_address: props.bankAccount.bank_address || '',
    bank_country: props.bankAccount.bank_country || '',
    bank_name: props.bankAccount.bank_name || '',
    beneficiary_name: props.bankAccount.beneficiary_name || '',
    iban: props.bankAccount.iban || '',
    swift: props.bankAccount.swift || '',
    bban: {
      ...bankAccountSaturated.value.bban,
    },
  }

  handleFormUpdate()
  handleCountryUpdated()

  showForm.value = true
})
</script>
